import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  HddOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import OemInfoIcon from "../../../assets/images/vectors/SideBarIcons/OemInfoIcon";
import { getChangedFields } from "../../../utils/getChangedFields";

export const OemInfoForm = ({ type }) => {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/oem-infos/${id}/`
    : `${process.env.REACT_APP_API_URL}/api/oem-infos/`;

  const { data, loading, error, refetch } = useGet(url);
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(url);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(url);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(url);

  const onFinish = async (values) => {
    try {
      if (id) {
        const changedFields = getChangedFields(data?.data, values);
        await putAction(changedFields);
      } else {
        await postAction(values);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    // form.resetFields();
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  //toat for create
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong"
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");

      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData, postLoading]);

  //toat for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong"
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (!id || loading) return;
    const getAccountInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            OemInfoName: String(data?.data?.OemInfoName),
            OemInfoDesc: data.data.OemInfoDesc
              ? String(data.data.OemInfoDesc)
              : "",
            LicenseAddr: String(data.data.LicenseAddr),
            LicensePort: String(data.data.LicensePort),
            CloudAddr: String(data.data.CloudAddr),
            CloudPort: String(data.data.CloudPort),
            StorageAddr: String(data.data.StorageAddr),
            StoragePort: String(data.data.StoragePort),
            StorageUser: String(data.data.StorageUser),
            StoragePass: String(data.data.StoragePass),
            SmtpAddr: String(data.data.SmtpAddr),
            SmtpPort: String(data.data.SmtpPort),
            SmtpUser: String(data.data.SmtpUser),
            SmtpPass:
              data.data.SmtpPass != null ? String(data.data.SmtpPass) : "",
            UpdateAddr: String(data.data.UpdateAddr),
            UpdatePort: String(data.data.UpdatePort),
            UpdateUser: String(data.data.UpdateUser),
            UpdatePass: String(data.data.UpdatePass),
          });
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          console.log({ error });
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, form, type]);
  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      // refetch();
      navigate("/oeminfo");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong"
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "OEM Info",
      href: "/oeminfo",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/oeminfo/info/${id}`
          : `/oeminfo/edit/${id}`
        : "/oeminfo/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/oeminfo/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      {
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div>
            <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
              <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
                <CommonButton buttonsData={buttonsData} />
              </CommonBreadcrumb>
            </div>
            {
              <>
                <div className="bg-white rounded-[4px] border overflow-hidden">
                  <div className="flex items-center bg-gray-200 p-2.5">
                    <OemInfoIcon className="text-[18px] text-black " />
                    <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                      Configuration Info
                    </span>
                  </div>
                  <div className="px-2 pt-1 pb-3">
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Name"
                          name="OemInfoName"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Description"
                          name="OemInfoDesc"
                          disabled={type === "info"}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="mt-4 bg-white rounded-[4px] border overflow-hidden">
                  <div className="flex items-center bg-gray-200 p-2.5">
                    <DatabaseOutlined className="text-[18px] text-black " />
                    <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                      Storage Server
                    </span>
                  </div>
                  <div className="px-2 pt-1 pb-3">
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Storage Address"
                          name="StorageAddr"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Storage Port"
                          name="StoragePort"
                          disabled={type === "info"}
                          required
                          type={"number"}
                        />
                      </Col>

                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Storage Username"
                          name="StorageUser"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Storage Password"
                          name="StoragePass"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="mt-4 bg-white rounded-[4px] border overflow-hidden">
                  <div className="bg-gray-200 p-2.5">
                    <HddOutlined className="text-[18px] text-black " />
                    <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                      SMTP Server
                    </span>
                  </div>
                  <div className="px-2 pt-1 pb-3">
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="SMTP Address"
                          name="SmtpAddr"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="SMTP Port"
                          name="SmtpPort"
                          disabled={type === "info"}
                          required
                          type={"number"}
                        />
                      </Col>

                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="SMTP Username"
                          name="SmtpUser"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="SMTP Password"
                          name="SmtpPass"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="mt-4 bg-white rounded-[4px] border overflow-hidden">
                  <div className="bg-gray-200 p-2.5">
                    <CloudServerOutlined className="text-[18px] text-black " />
                    <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                      Update Server
                    </span>
                  </div>
                  <div className="px-2 pt-1 pb-3">
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Update Address"
                          name="UpdateAddr"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Update Port"
                          name="UpdatePort"
                          disabled={type === "info"}
                          required
                          type={"number"}
                        />
                      </Col>

                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Update Username"
                          name="UpdateUser"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="Update Password"
                          name="UpdatePass"
                          disabled={type === "info"}
                          required
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </>
            }
          </div>
        </Form>
      }
      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
